/**
 * D-052 — solicitações do titular (LGPD, art. 18), registradas pelo gestor do município.
 * Resposta em até 15 dias (art. 19, II).
 */
import { z } from 'zod';
import { hojeEmBrasilia } from './esquemas-cadastro';

export const TIPOS_SOLICITACAO_TITULAR = [
  'confirmacao_acesso',
  'relatorio_acessos',
  'copia_prontuario',
  'correcao',
  'eliminacao',
  'outro',
] as const;
export type TipoSolicitacaoTitular = (typeof TIPOS_SOLICITACAO_TITULAR)[number];

export const ROTULO_SOLICITACAO_TITULAR: Record<TipoSolicitacaoTitular, string> = {
  confirmacao_acesso: 'Confirmação e acesso aos dados',
  relatorio_acessos: 'Quem acessou os dados',
  copia_prontuario: 'Cópia do prontuário',
  correcao: 'Correção de dados',
  eliminacao: 'Eliminação de dados',
  outro: 'Outro pedido',
};

export const RELACOES_SOLICITANTE = [
  'titular',
  'representante_legal',
  'responsavel_crianca',
] as const;
export const ROTULO_RELACAO_SOLICITANTE: Record<(typeof RELACOES_SOLICITANTE)[number], string> = {
  titular: 'A própria pessoa (titular)',
  representante_legal: 'Representante legal',
  responsavel_crianca: 'Responsável pela criança',
};

/** Prazo de resposta (LGPD, art. 19, II). */
export const PRAZO_TITULAR_DIAS = 15;

export const novaSolicitacaoTitularSchema = z.object({
  pacienteId: z.uuid({ message: 'Escolha a pessoa.' }),
  tipo: z.enum(TIPOS_SOLICITACAO_TITULAR, { message: 'Escolha o tipo de pedido.' }),
  solicitanteNome: z.string().trim().min(3, { message: 'Informe quem fez o pedido.' }).max(255),
  solicitanteRelacao: z.enum(RELACOES_SOLICITANTE),
  identificacaoConferida: z
    .string()
    .trim()
    .min(5, { message: 'Diga como a identidade foi conferida (ex.: RG apresentado).' })
    .max(500),
  descricao: z
    .string()
    .trim()
    .max(2000)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
  recebidaEm: z.iso
    .date({ message: 'Informe a data do pedido.' })
    .refine((d) => d <= hojeEmBrasilia(), {
      message: 'A data do pedido não pode estar no futuro.',
    }),
});
export type DadosNovaSolicitacaoTitular = z.infer<typeof novaSolicitacaoTitularSchema>;

export const responderSolicitacaoTitularSchema = z.object({
  situacao: z.enum(['atendida', 'recusada']),
  resposta: z
    .string()
    .trim()
    .min(10, { message: 'Descreva a resposta dada (mínimo de 10 caracteres).' })
    .max(4000),
});
export type DadosResponderSolicitacaoTitular = z.infer<typeof responderSolicitacaoTitularSchema>;

/** Rótulo das áreas do prontuário no relatório de acessos (tabela da auditoria → texto). */
export const ROTULO_AREA_AUDITORIA: Record<string, string> = {
  pacientes: 'Cadastro',
  gestacoes: 'Gestação',
  parceiros: 'Parceiro',
  prenatal: 'Pré-natal',
  exames: 'Exames',
  tratamentos: 'Tratamento',
  doses_tratamento: 'Doses',
  consultas_pre_natal: 'Consultas de pré-natal',
  maternidade: 'Maternidade',
  maternidades: 'Internação',
  partos: 'Parto',
  recem_nascidos: 'Recém-nascido',
  neonatologia: 'Conduta neonatal',
  seguimentos_crianca: 'Seguimento da criança',
  puericultura: 'Seguimento da criança',
  notificacoes_sinan: 'Notificação SINAN',
  documentos: 'Anexos',
  solicitacoes_titular: 'Solicitação do titular',
};

export const ROTULO_ACAO_AUDITORIA: Record<string, string> = {
  visualizacao: 'Viu',
  criacao: 'Registrou',
  alteracao: 'Alterou',
  substituicao: 'Corrigiu ("Novo")',
  exclusao: 'Excluiu',
  inativacao: 'Inativou',
  download: 'Baixou',
  geracao_pdf: 'Gerou a ficha',
  alta: 'Registrou a alta',
  confirmacao_conduta: 'Confirmou a conduta',
  cancelamento: 'Cancelou',
  reinicio: 'Reiniciou o tratamento',
  inicio_seguimento: 'Iniciou o seguimento',
  encerramento_seguimento: 'Encerrou o seguimento',
  download_pdf: 'Baixou a ficha',
  relatorio_acessos_titular: 'Gerou o relatório de acessos (pedido do titular)',
  copia_prontuario_titular: 'Gerou a cópia do prontuário (pedido do titular)',
};
